// Centralizes the naming scheme used when downloading generated assets.
//
// The agreed convention is `{slug}.{ext}` for single downloads and
// `{slug}_{N}.{ext}` for bulk downloads where N resets per group
// (per producto en el catálogo IA, per variante en el historial).
//
// El slug se deriva del nombre de la variante/producto: minúsculas,
// acentos removidos, todo lo no alfanumérico convertido a `_`.

const KNOWN_IMAGE_EXT_RE = /\.(png|jpe?g|webp|gif|bmp|tiff|svg)$/i;
const KNOWN_VIDEO_EXT_RE = /\.(mp4|webm|mov)$/i;

export const inferExtFromMime = (mime?: string): string => {
  if (!mime) return "";
  const m = mime.toLowerCase();
  if (m === "image/png") return "png";
  if (m === "image/jpeg") return "jpg";
  if (m === "image/webp") return "webp";
  if (m === "image/gif") return "gif";
  if (m === "image/bmp") return "bmp";
  if (m === "image/tiff") return "tiff";
  if (m === "image/svg+xml") return "svg";
  if (m === "video/mp4") return "mp4";
  if (m === "video/webm") return "webm";
  if (m === "video/quicktime") return "mov";
  return "";
};

const extFromOriginal = (fileName?: string): string => {
  if (!fileName) return "";
  const m =
    fileName.match(KNOWN_IMAGE_EXT_RE) || fileName.match(KNOWN_VIDEO_EXT_RE);
  return m ? m[0].slice(1).toLowerCase() : "";
};

/** Convierte un nombre arbitrario en un slug seguro para nombre de archivo:
 *  minúsculas, sin acentos, espacios y símbolos → `_`. Cae a `"imagen"` si
 *  no queda nada utilizable. */
export const slugifyVariantName = (name?: string | null): string => {
  const trimmed = (name || "").trim();
  if (!trimmed) return "imagen";
  const slug = trimmed
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return slug || "imagen";
};

export interface BuildDownloadFileNameOpts {
  /** Nombre de variante/producto. Se slugifica internamente. */
  variantName?: string | null;
  /** Si está definido, se appendea como `_{index}`. Omitir para single
   *  downloads (`chaqueta_beige.png`). 1-based por convención. */
  index?: number;
  /** Mime del blob — usado como fallback para inferir extensión. */
  mime?: string;
  /** Nombre original del archivo en Storage — usado como fuente prioritaria
   *  para la extensión cuando existe. */
  originalFileName?: string;
  /** Fuerza `.mp4` (cuando sabemos que es video pero el mime/fileName no
   *  lo refleja, p.ej. thumbnails .png de un video). */
  forceVideo?: boolean;
}

/** Construye un filename canónico para descargas según la convención
 *  acordada. Ver header del archivo. */
export const buildDownloadFileName = (opts: BuildDownloadFileNameOpts): string => {
  // Si el variantName está vacío o es el literal genérico "Imagen"
  // (fallback que setea GeneratedImagesInline cuando la imagen no tiene
  // variante asociada), preferimos usar el stem del archivo original
  // como slug — así el download name no termina siendo "imagen.png" o
  // "imagen_1.png" para CUALQUIER imagen sin variante.
  const rawVariantName = (opts.variantName ?? "").trim();
  const isGenericVariantName =
    !rawVariantName || rawVariantName.toLowerCase() === "imagen";
  const slugSource = isGenericVariantName
    ? opts.originalFileName
      ? // Sacamos extensión y sufijos de numeración antes de slugificar
        // para que `aab1c2_4k.png` quede como `aab1c2_4k`.
        opts.originalFileName.replace(/\.[a-z0-9]{1,5}$/i, "") ||
        opts.originalFileName
      : "imagen"
    : rawVariantName;
  const slug = slugifyVariantName(slugSource);
  const indexSuffix =
    typeof opts.index === "number" && Number.isFinite(opts.index)
      ? `_${opts.index}`
      : "";
  if (opts.forceVideo) return `${slug}${indexSuffix}.mp4`;
  const ext =
    extFromOriginal(opts.originalFileName) ||
    inferExtFromMime(opts.mime) ||
    "png";
  return `${slug}${indexSuffix}.${ext}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Extensión REAL a partir de los bytes
//
// El backend sube las generadas a Storage como `archivo_<ts>_<rand>.png` a
// propósito (paridad n8n/contratos) aunque Gemini devuelva JPEG; ADR-657 arregló
// el Content-Type servido pero dejó el NOMBRE. La web nunca lo nota porque el
// browser usa el Content-Type, pero el archivo llega al disco mintiendo y
// **Finder resuelve el tipo por extensión**: le pide la miniatura al decodificador
// de PNG, que no puede con bytes JPEG, y muestra el ícono genérico.
// Ver ADR-2026-09-08-descargas-con-extension-que-miente.
//
// Por qué los magic bytes y no `blob.type`: las imágenes ANTERIORES a ADR-657
// tienen el Content-Type `image/png` hardcodeado, así que también mienten. Los
// bytes no.
// ─────────────────────────────────────────────────────────────────────────────

/** Extensiones que consideramos equivalentes para un mismo formato: si el nombre
 *  ya usa una de ellas, no lo tocamos (renombrar `.jpeg` → `.jpg` sería ruido). */
const IMAGE_EXT_ALIASES: Record<string, readonly string[]> = {
  png: ["png"],
  jpg: ["jpg", "jpeg"],
  webp: ["webp"],
  gif: ["gif"],
};

const startsWith = (bytes: Uint8Array, sig: readonly number[], offset = 0): boolean =>
  bytes.length >= offset + sig.length && sig.every((b, i) => bytes[offset + i] === b);

const asciiAt = (bytes: Uint8Array, offset: number, text: string): boolean =>
  startsWith(bytes, [...text].map((c) => c.charCodeAt(0)), offset);

/**
 * Extensión real de una imagen según sus MAGIC BYTES, o `""` si no reconocemos
 * el formato. Deliberadamente sólo conoce imágenes rasterizadas: cualquier otra
 * cosa —mp4, mov, pdf, zip, xml, svg (que es texto)— cae en `""` y su nombre
 * queda intacto. Es la primera de las dos redes que impiden que esto toque un
 * video. Espeja `sniffImageMime` de `atlas-backend/src/lib/imageFormat.ts`.
 */
export const sniffImageExtFromBytes = (bytes: Uint8Array): string => {
  if (!bytes || bytes.length < 4) return "";
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return "jpg";
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "png";
  if (bytes.length >= 12 && asciiAt(bytes, 0, "RIFF") && asciiAt(bytes, 8, "WEBP")) return "webp";
  if (bytes.length >= 6 && (asciiAt(bytes, 0, "GIF87a") || asciiAt(bytes, 0, "GIF89a"))) {
    return "gif";
  }
  return "";
};

/**
 * Devuelve `fileName` con la extensión que corresponde a los bytes. Conservador
 * por diseño: sólo reemplaza cuando **las dos** condiciones se cumplen —los bytes
 * son una imagen conocida Y el nombre ya termina en una extensión de imagen—, así
 * que un video, un PDF, un ZIP o un nombre sin extensión salen tal como entraron.
 */
export const fixExtensionFromBytes = (fileName: string, bytes: Uint8Array): string => {
  const real = sniffImageExtFromBytes(bytes);
  if (!real) return fileName;
  const match = fileName.match(KNOWN_IMAGE_EXT_RE);
  if (!match) return fileName;
  const actual = match[0].slice(1).toLowerCase();
  if ((IMAGE_EXT_ALIASES[real] ?? [real]).includes(actual)) return fileName;
  return `${fileName.slice(0, fileName.length - match[0].length)}.${real}`;
};

/**
 * Igual que `fixExtensionFromBytes` pero leyendo la cabecera del blob (16 bytes).
 * Ante CUALQUIER error devuelve el nombre original: una extensión imperfecta es
 * infinitamente mejor que una descarga que no ocurre.
 */
export const fixExtensionFromBlob = async (fileName: string, blob: Blob): Promise<string> => {
  try {
    const head = await blob.slice(0, 16).arrayBuffer();
    return fixExtensionFromBytes(fileName, new Uint8Array(head));
  } catch {
    return fileName;
  }
};
